import { useI18n } from '@/hooks/useI18n';
import { Skeleton } from '@/components/ui/skeleton';
import { Dot } from '@/components/primitives/Dot';
import type { SchedulerStatusResponse } from '@/types/api';
import type { SchedulerRunType } from '@/types/enums';
import { SCHEDULER_RUN_TYPES } from '@/types/enums';
import { formatDuration, formatRelative } from '@/lib/format';

const LABEL: Record<SchedulerRunType, [string, string]> = {
  MAIN_3H: ['Cycle principal', 'Main cycle'],
  NIGHTLY_PROFILE: ['Profile refresh', 'Profile refresh'],
  HOURLY_ROLLUP: ['Rollup horaire', 'Hourly rollup'],
  DAILY_ROLLUP: ['Rollup journalier', 'Daily rollup'],
};

const SUBTITLE: Record<SchedulerRunType, [string, string]> = {
  MAIN_3H: ['toutes les 8h', 'every 8 hours'],
  NIGHTLY_PROFILE: ['chaque nuit', 'every night'],
  HOURLY_ROLLUP: ['chaque heure', 'every hour'],
  DAILY_ROLLUP: ['fin de journée', 'end of day'],
};

interface RunsStripProps {
  data: SchedulerStatusResponse | undefined;
  loading?: boolean;
}

export function RunsStrip({ data, loading }: RunsStripProps) {
  const { t } = useI18n();
  if (loading) {
    return (
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {SCHEDULER_RUN_TYPES.map((type) => (
          <Skeleton key={type} className="h-[100px] w-full rounded-[14px]" />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      {SCHEDULER_RUN_TYPES.map((type) => {
        const run = data?.lastRuns?.[type] ?? null;
        const variant = run
          ? run.status === 'OK'
            ? 'ok'
            : run.status === 'RUNNING'
              ? 'running'
              : run.status === 'FAILED'
                ? 'fail'
                : 'warn'
          : 'idle';
        return (
          <article
            key={type}
            className="flex min-w-0 flex-col gap-2 rounded-[14px] border border-line-1 bg-bg-1 px-3 py-3.5 shadow-card sm:px-4"
          >
            <header className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[12px] font-semibold text-text-1">{t(...LABEL[type])}</div>
                <div className="text-[11px] text-text-3">{t(...SUBTITLE[type])}</div>
              </div>
              <Dot variant={variant} pulse={variant === 'running'} />
            </header>
            <div className="font-display text-[18px] tabular-nums text-text-1">
              {run ? formatDuration(run.durationMs) : '—'}
            </div>
            <div className="text-[11px] text-text-3">
              {run ? formatRelative(run.startedAt) : t('jamais exécuté', 'never run')}
            </div>
          </article>
        );
      })}
    </div>
  );
}
